/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import {
  Stack,
  Title,
  Paper,
  Text,
  SimpleGrid,
  Select,
  TextInput,
  Button,
  Card,
  Grid,
  Badge,
  Group,
  Modal,
  Textarea,
  Box,
  Loader,
  Center,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import {
  IconCalendarEvent,
  IconUsers,
  IconVideo,
  IconFolder,
  IconSearch,
  IconEdit,
  IconX,
  IconEye,
  IconPlayerPlay,
} from "@tabler/icons-react";
import { useGetMeetings } from "api/meeting/getAllMeeting";
import MeetingDetailsModal from "../meetingDetail/meetingDetailModal";
import UpdateMeetingModal from "../updateModal/updateMeeting";
import CancelMeetingModal from "../cancelModal/cancelModal";

export type Attendee = {
  user: string;
  userType: string;
  status?: "pending" | "accepted" | "declined";
};

export type Meeting = {
  id: string;

  title: string;
  description?: string;
  scheduledDate: string;
  duration?: number;
  status: "scheduled" | "ongoing" | "completed" | "cancelled";
  meetingPlatform?: string;
  meetingLink?: string;
  attendees: Attendee[];
  agenda?: string;
  projectId?: string | { _id?: string; name?: string } | null;
  isRecurring?: boolean;
};

const initialMeetings: Meeting[] = [];

export default function AllMeetings(): JSX.Element {
  const [params, setParams] = useState<{
    page: number;
    limit: number;
    status: string;
    meetingType: string;
    startDate: string;
    endDate: string;
    projectId: string;
  }>({
    page: 1,
    limit: 10,
    status: "",
    meetingType: "",
    startDate: "",
    endDate: "",
    projectId: "",
  });

  const [filterStatus, setFilterStatus] = useState<string | null>("");
  const [filterType, setFilterType] = useState<string | null>("");
  const [filterStart, setFilterStart] = useState<string | null>("");
  const [filterEnd, setFilterEnd] = useState<string | null>("");
  const [openedCancelModal, setOpenedCancelModal] = useState(false);
  const [meetings, setMeetings] = useState<Meeting[]>(initialMeetings);

  const [openedMeetingModal, setOpenedMeetingModal] = useState(false);
  const [currentMeetingId, setCurrentMeetingId] = useState<string | null>(null);

  const { data, isLoading, isError } = useGetMeetings(params);

  useEffect(() => {
    if (!data?.data?.meetings) return;
    const mapped = data.data.meetings.map((m: any) => {
      const attendees = Array.isArray(m.attendees)
        ? m.attendees.map((a: any) => ({
            user:
              a.user && typeof a.user === "object"
                ? a.user._id ?? ""
                : a.user ?? "",
            userType: a.userType ?? "User",
            status: a.status ?? "pending",
          }))
        : [];

      return {
        id: m.id ?? m._id,
        title: m.title,
        description: m.description,
        scheduledDate: m.scheduledDate,
        duration: m.duration,
        status: m.status,
        meetingPlatform: m.meetingPlatform,
        meetingLink: m.meetingLink,
        attendees,
        agenda: m.agenda,
        projectId: m.projectId ?? null,
        isRecurring: m.isRecurring,
      } as Meeting;
    });

    setMeetings(mapped);
  }, [data]);

  useEffect(() => {
    const today = new Date();
    const nextWeek = new Date(today);
    nextWeek.setDate(nextWeek.getDate() + 7);
    setFilterStart(today.toISOString().slice(0, 10));
    setFilterEnd(nextWeek.toISOString().slice(0, 10));

    setParams((p) => ({
      ...p,
      startDate: today.toISOString(),
      endDate: nextWeek.toISOString(),
    }));
  }, []);

  const currentMeeting = useMemo(
    () => meetings.find((m) => m.id === currentMeetingId) || null,
    [meetings, currentMeetingId]
  );

  const badgeColor = (status: string) =>
    status === "scheduled"
      ? "teal"
      : status === "ongoing"
      ? "yellow"
      : status === "completed"
      ? "blue"
      : "red";

  const displayProject = (p?: string | { name?: string } | null) => {
    if (!p) return "Internal";
    if (typeof p === "string") return p;
    return (p as any).name ?? "Internal";
  };

  function editMeeting(meetingId: string) {
    showNotification({ message: `Open edit for ${meetingId}`, color: "blue" });
  }
  const [opened, setOpened] = useState(false);
  const [loginUser, setLoginUser] = useState({
    id: "",
    userName: "",
  });
  const handleClose = async () => {
    setOpened(false);
  };

  function cancelMeeting(meetingId: string) {
    const confirmed = window.confirm(
      "Are you sure you want to cancel this meeting?"
    );
    if (!confirmed) return;

    setMeetings((s) =>
      s.map((m) => (m.id === meetingId ? { ...m, status: "cancelled" } : m))
    );
    showNotification({
      message: "Meeting cancelled successfully",
      color: "green",
    });
  }

  function viewMeeting(meetingId: string) {
    setOpenedMeetingModal(true);
  }

  function joinMeeting(meeting: Meeting) {
    showNotification({ message: "Joining meeting...", color: "blue" });
    if (meeting.meetingLink) window.open(meeting.meetingLink, "_blank");
  }

  const applyFilters = () => {
    const nextParams = {
      ...params,
      page: 1,
      status: filterStatus ?? "",
      meetingType: filterType ?? "",
      startDate: filterStart ? new Date(filterStart).toISOString() : "",
      endDate: filterEnd ? new Date(filterEnd).toISOString() : "",
    };

    setParams(nextParams);
    showNotification({ message: "Filters applied", color: "blue" });
  };

  const filteredMeetings = useMemo(() => {
    return meetings.filter((m) => {
      if (filterStatus && filterStatus !== "" && m.status !== filterStatus)
        return false;
      if (filterType && filterType !== "") {
        if (filterType === "internal" && m.projectId) return false;
        if (filterType === "client" && !m.projectId) return false;
      }
      const startTs = filterStart
        ? new Date(filterStart).setHours(0, 0, 0, 0)
        : null;
      const endTs = filterEnd
        ? new Date(filterEnd).setHours(23, 59, 59, 999)
        : null;
      const mTs = new Date(m.scheduledDate).getTime();
      if (startTs && mTs < startTs) return false;
      if (endTs && mTs > endTs) return false;
      return true;
    });
  }, [meetings, filterStatus, filterType, filterStart, filterEnd]);

  return (
    <Stack spacing="md">
      <Title order={3}>All Meetings</Title>

      <Paper p="md" radius="md" withBorder>
        <Text weight={700} mb="sm">
          Filter Meetings
        </Text>

        <SimpleGrid
          cols={4}
          breakpoints={[
            { maxWidth: "md", cols: 2 },
            { maxWidth: "sm", cols: 1 },
          ]}
        >
          <Select
            data={[
              { value: "", label: "All Status" },
              { value: "scheduled", label: "Scheduled" },
              { value: "ongoing", label: "Ongoing" },
              { value: "completed", label: "Completed" },
              { value: "cancelled", label: "Cancelled" },
            ]}
            value={filterStatus ?? ""}
            onChange={(v) => setFilterStatus(v ?? "")}
            placeholder="Status"
          />

          <Select
            data={[
              { value: "", label: "All Types" },
              { value: "internal", label: "Internal" },
              { value: "client", label: "Client" },
              { value: "mixed", label: "Mixed" },
            ]}
            value={filterType ?? ""}
            onChange={(v) => setFilterType(v ?? "")}
            placeholder="Meeting Type"
          />

          <TextInput
            value={filterStart ?? ""}
            onChange={(e) => setFilterStart(e.currentTarget.value)}
            type="date"
          />

          <TextInput
            value={filterEnd ?? ""}
            onChange={(e) => setFilterEnd(e.currentTarget.value)}
            type="date"
          />

          <Button onClick={applyFilters} leftIcon={<IconSearch />}>
            Apply Filters
          </Button>
        </SimpleGrid>
      </Paper>

      {isLoading ? (
        <Center style={{ padding: 24 }}>
          <Loader />
          <Text ml="sm">Loading meetings...</Text>
        </Center>
      ) : isError ? (
        <Text color="red">Failed to load meetings. Please try again.</Text>
      ) : (
        <Stack>
          {filteredMeetings.map((m) => (
            <Card key={m.id} shadow="sm" p="lg" radius="md" withBorder>
              <Grid>
                <Grid.Col span={8}>
                  <Text weight={700}>{m.title}</Text>
                  <Text size="sm" color="dimmed">
                    <IconCalendarEvent
                      size={14}
                      style={{ verticalAlign: "middle", marginRight: 6 }}
                    />
                    {new Date(m.scheduledDate).toLocaleDateString()} |{" "}
                    {new Date(m.scheduledDate).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </Text>
                </Grid.Col>

                <Grid.Col
                  span={4}
                  style={{ display: "flex", justifyContent: "flex-end" }}
                >
                  <Badge color={badgeColor(m.status)}>
                    {m.status.toUpperCase()}
                  </Badge>
                </Grid.Col>
              </Grid>

              <Group mt="md">
                <Group spacing={6}>
                  <IconUsers />
                  <Text size="sm">{m.attendees?.length ?? 0} Attendees</Text>
                </Group>

                <Group spacing={6}>
                  <IconVideo />
                  <Text size="sm">{m.meetingPlatform || "N/A"}</Text>
                </Group>

                <Group spacing={6}>
                  <IconFolder />
                  <Text size="sm">{displayProject(m.projectId)}</Text>
                </Group>
              </Group>

              <Group mt="md">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setCurrentMeetingId(m.id);
                    setOpened(true);
                  }}
                  leftIcon={<IconEdit />}
                >
                  Edit
                </Button>
                <Button
                  size="sm"
                  color="red"
                  onClick={() => {
                    setCurrentMeetingId(m.id);
                    setOpenedCancelModal(true);
                  }}
                  leftIcon={<IconX />}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  variant="subtle"
                  onClick={() => {
                    setCurrentMeetingId(m.id);
                    viewMeeting(m.id);
                  }}
                  leftIcon={<IconEye />}
                >
                  View
                </Button>
              </Group>
            </Card>
          ))}
        </Stack>
      )}
      <MeetingDetailsModal
        opened={openedMeetingModal}
        onClose={() => setOpenedMeetingModal(false)}
        currentMeetingId={currentMeetingId}
        onJoin={joinMeeting}
        onEdit={editMeeting}
        onCancel={cancelMeeting}
      />
      <UpdateMeetingModal
        currentMeetingId={currentMeetingId}
        opened={opened}
        onClose={handleClose}
      />
      <CancelMeetingModal
        opened={openedCancelModal}
        currentMeetingId={currentMeetingId}
        onClose={() => setOpenedCancelModal(false)}
        // onConfirm={(reason) => confirmCancelMeeting(reason)}
      />
    </Stack>
  );
}
